import { from as observableFrom, BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import
{
    IHttpRequestConfig,
    IHttpResponse,
    INetworkError
} from "./types/http.types";
import
{
    IHttpHeader
} from "./http.provider.response.interceptor";
import axios from "axios";
import { RequestInterceptor } from "./http.provider.request.interceptor";
import { ResponseInterceptor } from "./http.provider.response.interceptor";
import { HttpDelegate } from './http.delegate';
import { HttpCodes, ServiceEndpointConstants } from '../ServiceUtil/consts';
import { SpinnerService } from '../SpinnerService';
import { IAppConfig } from './types/IApplicationConfig';

class IHttpHeaderObservable
{
    name: string;
    subject: BehaviorSubject<IHttpHeader>;
    observable: Observable<IHttpHeader>;
}

/**
 * Main class for this module.  This exposes an API to clients for making http calls
 */
export class HttpProvider
{
    /**
     * private subject for delivering network errors through the public onNetworkError observable
     */
    private onNetworkErrorSubject: BehaviorSubject<INetworkError> = new BehaviorSubject(null);

    /*
     * Observable that will fire whenever a network error (HTTP error or timeout) is encountered
     */
    public onNetworkError: Observable<INetworkError> = this.onNetworkErrorSubject.pipe(filter((error) => error !== null));

    /**
     * private subject for delivering network responses through the public onNetworkSuccess observable
     */
    private onNetworkSuccessSubject: BehaviorSubject<IHttpResponse> = new BehaviorSubject(null);

    /*
     * Observable that will fire whenever a network response (HTTP 200) is encountered
     */
    public onNetworkSuccess: Observable<IHttpResponse> = this.onNetworkSuccessSubject.pipe(filter((response) => response !== null));

    /**
     * Subject that fires when session is auto-extended by the server
     */
    private onSessionExtendedSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);

    /**
     * Observable that fires when session is automatically extended.
     * The new backend auto-extends sessions on any API call, eliminating
     * the need for frequent Resume API calls.
     */
    public onSessionExtended: Observable<boolean> = this.onSessionExtendedSubject.pipe(filter((extended) => extended === true));

    /**
     * Maximum number of times that the http service will retry an API call for generic HTTP and API Failures.
     * @type {number}
     */
    public static MAX_RETRIES = 1;

    /**
     * Number of milliseconds that http service will wait before retrying an API call that failed
     * @type {number}
     */
    public static RETRY_WAIT_TIME_MS = 2000;

    /**
     * Header name for session extension notification from server
     */
    private static readonly SESSION_EXTENDED_HEADER = 'x-token-refreshed';

    /**
     * Constructor function will initialize the private data members, and then register the request interceptor and
     * response interceptors with Axios.
      */
    constructor(requestInterceptor: RequestInterceptor,
        responseInterceptor: ResponseInterceptor,
        private spinnerService: SpinnerService,
        private appConfig: IAppConfig)
    {
        // Validate configuration
        if (!appConfig || !appConfig.apiEndpoint)
        {
            throw new Error('HttpProvider requires valid IAppConfig with apiEndpoint');
        }

        // Use environment-based API URL from appConfig
        axios.defaults.baseURL = appConfig.apiEndpoint;

        // Configure for cross-origin cookie forwarding
        // This is critical when frontend domain (local.synergyportal.net) 
        // differs from API domain (localhost:44393)
        axios.defaults.withCredentials = true;

        // Add default headers for cross-origin requests
        axios.defaults.headers.common['Content-Type'] = 'application/json';
        axios.defaults.headers.common['Accept'] = 'application/json';

        console.group('🌐 HttpProvider Configuration');
        console.log('📍 Frontend domain:', window.location.origin);
        console.log('📍 API endpoint:', appConfig.apiEndpoint);
        console.log('🍪 withCredentials enabled:', true);
        console.log('🔧 Domain name config:', appConfig.domainName);

        // Validate cross-origin setup
        const frontendOrigin = window.location.origin;
        const apiOrigin = new URL(appConfig.apiEndpoint).origin;
        const isCrossOrigin = frontendOrigin !== apiOrigin;

        console.log('🔀 Cross-origin request:', isCrossOrigin);
        if (isCrossOrigin)
        {
            console.warn('⚠️ Cross-origin detected - ensure server CORS is configured for:');
            console.warn('   • Origin:', frontendOrigin);
            console.warn('   • AllowCredentials: true');
            console.warn('   • Expose headers: X-Token-Refreshed, X-Session-Extended');
        }
        console.groupEnd();

        // Set up request interceptor
        axios.interceptors.request.use(requestInterceptor.onRequest);

        // Set up response interceptor with session extension checking
        axios.interceptors.response.use(
            (response) =>
            {
                // Check for session extension header
                this.checkSessionExtension(response);
                const transformedResponse = responseInterceptor.onResponse(response);
                return transformedResponse as any; // Type assertion for compatibility
            },
            (error) =>
            {
                // Enhanced CORS and authentication error debugging
                if (error.response && error.response.status === 401)
                {
                    console.group('🚨 401 Unauthorized Error');
                    console.warn('❌ Authentication failed - user session invalid or expired');
                    console.log('🌐 Request origin:', window.location.origin);
                    console.log('📍 Request URL:', error.config?.url);

                    // Check if request headers were properly set
                    if (error.config && error.config.headers)
                    {
                        const hasAuthHeaders = Object.keys(error.config.headers)
                            .some(key => key.toLowerCase().includes('cookie') || key.toLowerCase().includes('authorization'));
                        console.log('🔑 Auth headers present:', hasAuthHeaders);
                    }

                    console.warn('💡 Possible causes:');
                    console.warn('   • Session expired or invalid');
                    console.warn('   • Server CORS not configured for credentials');
                    console.warn('   • Cookies not being sent cross-origin');
                    console.warn('   • Server rejecting X-Access-Token cookie');
                    console.groupEnd();

                    // Trigger 401 error handling - will clear session and redirect to login
                    this.handle401Error();
                }

                // Enhanced CORS error detection and guidance
                if (error.code === 'ERR_NETWORK' || 
                    (error.message && error.message.includes('CORS')) ||
                    (error.response && error.response.status === 0))
                {
                    console.group('🚨 CORS/Network Error');
                    console.error('❌ Cross-origin request blocked or network error');
                    console.log('📍 Request URL:', error.config ? error.config.url : 'unknown');
                    console.log('🌐 Frontend:', window.location.origin);
                    console.log('🎯 API endpoint:', (error.config && error.config.baseURL) ? error.config.baseURL : 'undefined');

                    console.warn('💡 Required server CORS configuration:');
                    console.warn(`   • AllowOrigin: ${window.location.origin}`);
                    console.warn('   • AllowCredentials: true');
                    console.warn('   • AllowMethods: GET, POST, PUT, DELETE, OPTIONS');
                    console.warn('   • AllowHeaders: Content-Type, Authorization');
                    console.warn('   • ExposedHeaders: X-Token-Refreshed, X-Session-Extended');
                    console.groupEnd();
                }

                return Promise.reject(error);
            }
        );
    }

    /**
     * Check if the server has automatically extended the session.
     * This is part of the new JWT authentication system that uses HTTP-only cookies:
     * - X-Access-Token: Auto-refreshed access token
     * - X-Refresh: Long-lived refresh token  
     * - X-Token-Refreshed: Header indicating cookies were updated
     */
    private checkSessionExtension(response: any): void
    {
        if (response && response.headers)
        {
            // Check both possible case variations of the session extension header
            const tokenRefreshed = response.headers[HttpProvider.SESSION_EXTENDED_HEADER] ||
                response.headers[HttpProvider.SESSION_EXTENDED_HEADER.toLowerCase()] ||
                response.headers['X-Token-Refreshed'];

            if (tokenRefreshed === 'true' || tokenRefreshed === true)
            {
                console.log('🔄 Session automatically extended by server (X-Access-Token & X-Refresh cookies refreshed)');
                this.onSessionExtendedSubject.next(true);
                // Reset after notifying
                setTimeout(() => this.onSessionExtendedSubject.next(false), 100);
            }
        }
    }

    /**
     * Handle 401 Unauthorized errors by clearing session and redirecting to login.
     * This prevents infinite loops and spinner issues when authentication fails.
     */
    private handle401Error(): void
    {
        console.warn('🔒 Handling 401 Unauthorized - clearing session and redirecting to login');

        // Stop any spinners that might be active
        this.spinnerService.resetSpinner();

        // Clear any cached authentication data
        // Note: We don't have direct access to AuthenticationService here,
        // so we emit a network error that will trigger proper logout handling
        const unauthorizedError: INetworkError = {
            code: HttpCodes.UNAUTHORIZED,
            message: 'Session expired or invalid. Please log in again.',
            data: null,
            url: '',
            detailedMessage: 'Authentication failed - session expired or invalid'
        };

        this.onNetworkErrorSubject.next(unauthorizedError);

        // Redirect to login page after a short delay to allow error handling
        setTimeout(() => {
            const loginUrl = '/auth/login';
            const currentPath = window.location.pathname;

            // Don't redirect if already on login page
            if (!currentPath.includes('login') && !currentPath.includes('join')) {
                console.log('🔀 Redirecting to login page from:', currentPath);
                window.location.href = loginUrl;
            }
        }, 100);
    }

    /**
     * Determine the API endpoint based on the current environment
     * @returns {string} The API host to use
     */
    private getApiEndpoint(): string
    {
        const host = window.location.host;
        
        // If running on localhost with webpack dev server, use the same host
        // (webpack-dev-server will proxy /api requests to the backend)
        if (host.includes('localhost') || host.includes('127.0.0.1')) {
            return host;
        }
        
        // If running on production or other environment, explicitly specify the API host
        // Update this based on your actual API server location
        if (host.includes('local.synergyportal.net')) {
            // For IIS-hosted frontend, point to the backend API server
            // Replace with your actual backend API address
            return 'api.local.synergyportal.net'; // or 'localhost:44392' or your API server
        }
        
        // Default fallback
        return host;
    }

    /**
     * Perform an http get
     * @param url that being requested
     * @param data object (optional) that describes the body of the get request
     * @param config object (optional) with additional information about the get request
     * @returns {Observable<any>} subscribe to this to get the results from the get request
     */
    public get(url: string, data?: any, config?: IHttpRequestConfig): Observable<any>
    {
        if (data)
        {
            config = config || {};
            config.data = data;
        }

        return this.http("get", url, config);
    }

    /**
     * Perform an basic http post
     * @param url that is being requested
     * @param data object (optional) with a body for the post request
     * @param config object (optional) with additional information about the post request
     * @returns {Observable<any>} subscribe to this to get the results from the post request
     */
    public post(url: string, data?: any, config?: IHttpRequestConfig): Observable<any>
    {
        return this.http("post", url, data, config);
    }

    /**
    * Perform an http put
    * @param url being requested
    * @param data (optional) request body
    * @param config (optional) extra config
    * @returns {Observable<any>}
   */
    public put(url: string, data?: any, config?: IHttpRequestConfig): Observable<any>
    {
        return this.http("put", url, data, config);
    }

    /**
     * Perform an http delete
     * @param url being requested
     * @param data (optional) request body
     * @param config (optional) extra config
     * @returns {Observable<any>}
    */
    public delete(url: string, data?: any, config?: IHttpRequestConfig): Observable<any>
    {
        return this.http("delete", url, data, config);
    }

    /**
     * General purpose function for making http requests.  Will have axios make the request based on the request
     * type, and will convert the axios promise into an observable.  If there are certain HTTP or API failures, then
     * the function will perform one retry after a wait time.  If the retry fails, or the HTTP or API error code is
     * not subject to a retry, then the error will be passed onto the caller.
     *
     * @param type of http request to make ("get", "post")
     * @param url of the http request
     * @param data optional additional payload data for the request
     * @param config optional axios config object to further customize the request
     * @returns {Observable<R|T>}
     */
    private http(type: string,
        url: string,
        data?: any,
        config?: IHttpRequestConfig): Observable<any>
    {
        const self = this;

        return observableFrom(axiosHttp(type, url, data, config));

        /**
         * Call the axios library for the given http type.  Deal with success and failure of the call
         */
        function axiosHttp(type: string, url: string, data?: any, config?: any, retries?: number)
        {
            let retry = (retries !== undefined) ? retries : 0;

            // Don't show spinner for auth refresh calls
            const isAuthCall = url === ServiceEndpointConstants.endpoints.AUTH.REFRESH ||
                url.indexOf('authstatus') > -1;

            if (!isAuthCall)
            {
                self.spinnerService.requestStarted();
            }

            return HttpProvider.makeHttpCall(type, url, data, config)
                .then(handleSuccess)
                .catch(handleError);

            function handleSuccess(result: IHttpResponse)
            {
                const responseConfig = config ? config : { isRaw: false };
                responseConfig.url = url;

                if (!isAuthCall)
                {
                    self.spinnerService.requestEnded();
                }

                self.onNetworkSuccessSubject.next(
                    {
                        data: result,
                        status: 200,
                        statusText: "OK",
                        headers: {},
                        config: responseConfig
                    });

                if (result && result.config && result.config.isRaw)
                {
                    result = result.data;
                }

                return result;
            }

            function handleError(error)
            {
                if (!isAuthCall)
                {
                    self.spinnerService.requestEnded();
                }

                let networkError = error;

                if (error.response || !error.code)
                {
                    const response = error.response ? error.response : { config: error.config };
                    networkError = HttpDelegate.getHTTPError(response);
                    self.onNetworkErrorSubject.next(networkError);
                }
                else
                {
                    self.onNetworkSuccessSubject.next(networkError);
                }

                const retried = handleErrorCode(networkError);

                if (retried) { return retried; }

                throw networkError;

                function handleErrorCode(error): Promise<any>
                {
                    if (retry++ < HttpProvider.MAX_RETRIES)
                    {
                        switch (error.code)
                        {
                            case HttpCodes.BAD_GATEWAY:
                            case HttpCodes.SERVICE_UNAVAILABLE:
                            case HttpCodes.GATEWAY_TIMEOUT:
                            case HttpCodes.NETWORK_TIMEOUT:
                                return new Promise((resolve) =>
                                {
                                    setTimeout(() =>
                                    {
                                        resolve(axiosHttp(type, url, data, config, retry));
                                    },
                                        HttpProvider.RETRY_WAIT_TIME_MS);
                                });
                        }

                        throw error;
                    }
                }
            }
        }
    }

    /**
     * This function exists as a public static member so that we can spy on it in unit testing and test the retry
     * capabilities of the service
     */
    public static makeHttpCall(type: string, url: string, data?: any, config?: IHttpRequestConfig, retries?: number)
    {
        // Ensure withCredentials is always set for cookie forwarding
        if (!config)
        {
            config = {};
        }

        // Always enable credentials to ensure cookies are sent with requests
        // This is CRITICAL for cross-origin JWT cookie forwarding
        config.withCredentials = true;

        // If data is FormData, ensure Content-Type is not set so axios can set it automatically with boundary
        if (data instanceof FormData)
        {
            if (!config.headers)
            {
                config.headers = {
                    'Content-Type': 'multipart/form-data',
                };
            }
        }

        // Enhanced debugging for cookie forwarding issues
        const currentOrigin = window.location.origin;
        const targetUrl = url.startsWith('http') ? url : (config.baseURL || axios.defaults.baseURL) + url;
        const isLocalhost = window.location.hostname === 'localhost';
        const isCustomDomain = window.location.hostname.includes('synergyportal.net');
        
        let isCrossOrigin = false;
        try {
            const targetUrlObj = new URL(targetUrl);
            isCrossOrigin = currentOrigin !== targetUrlObj.origin;
        } catch (e) {
            console.warn('Could not parse target URL for cross-origin check:', targetUrl);
            isCrossOrigin = true; // Assume cross-origin if we can't parse
        }

        if (isCrossOrigin || isCustomDomain)
        {
            console.group('🌐 Cross-origin HTTP request');
            console.log('📍 From:', currentOrigin);
            console.log('📍 To:', targetUrl);
            console.log('🍪 withCredentials:', config.withCredentials);
            console.log('🔀 Cross-origin:', isCrossOrigin);
            console.log('🎯 Request type:', type.toUpperCase());
            console.log('📦 Data type:', data instanceof FormData ? 'FormData' : typeof data);

            // Check if cookies exist in browser
            const hasCookies = document.cookie.length > 0;
            console.log('🍪 Browser has cookies:', hasCookies);
            if (hasCookies)
            {
                // Don't log actual cookie values for security
                const cookieCount = document.cookie.split(';').length;
                console.log('🍪 Cookie count:', cookieCount);
            }
            console.groupEnd();
        }

        return axios[type](url, data, config);
    }
}
